import React, { useMemo, useState, useCallback } from "react";
import { format, isValid } from "date-fns";
import * as Popover from "@radix-ui/react-popover";
import { DayPicker } from "react-day-picker";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

import "react-day-picker/dist/style.css";
import "../styles/shadDatePicker.css";

const getDisplayDate = (date, dateFormat) => {
  if (!date || !isValid(date)) {
    return "";
  }
  try {
    return format(date, dateFormat);
  } catch {
    return "";
  }
};

const coerceDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }

  const coerced = new Date(value);
  return isValid(coerced) ? coerced : null;
};

const ShadDatePicker = ({
  label,
  placeholder,
  value,
  onChange,
  dateFormat = "dd/MM/yyyy",
  width = 240,
  disabled = false,
  align = "start",
  weekStartsOn = 1,
}) => {
  const [open, setOpen] = useState(false);
  const parsedValue = useMemo(() => coerceDate(value), [value]);
  const resolvedWidth = typeof width === "number" ? `${width}px` : width;
  const placeholderText = placeholder || label || "Select date";
  const displayText = parsedValue
    ? getDisplayDate(parsedValue, dateFormat)
    : placeholderText;

  const handleSelect = useCallback(
    (selectedDate) => {
      if (typeof onChange === "function") {
        onChange(selectedDate ?? null);
      }
      if (selectedDate) {
        setOpen(false);
      }
    },
    [onChange]
  );

  const handleOpenChange = useCallback(
    (nextOpen) => {
      if (disabled) return;
      setOpen(nextOpen);
    },
    [disabled]
  );

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="shad-date-trigger"
          aria-label={placeholderText}
          style={{ width: resolvedWidth }}
          disabled={disabled}
        >
          <CalendarMonthIcon fontSize="small" className="shad-date-icon" />
          <span className={parsedValue ? "shad-date-value" : "shad-date-placeholder"}>
            {displayText}
          </span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="shad-date-popover"
          align={align}
          sideOffset={8}
        >
          <DayPicker
            className="shad-rdp"
            mode="single"
            selected={parsedValue ?? undefined}
            onSelect={handleSelect}
            weekStartsOn={weekStartsOn}
            initialFocus
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default ShadDatePicker;

